import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import FlagIcon from "@mui/icons-material/Flag";
import CheckIcon from "@mui/icons-material/Check";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { fetchPendingFollowups, completeFollowupStep, ignoreFollowupStep } from "../../utils/eventApi";
import { theme } from "../../theme/colors";

const COLLAPSED_KEY = "dashboard_followup_actions_collapsed";

function formatEventEndTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return `Ended: ${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })} at ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
}

export default function FollowUpActionsSection({ onRefresh }) {
  const [pendingEvents, setPendingEvents] = useState([]);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [completingStep, setCompletingStep] = useState(null);
  const [ignoringStep, setIgnoringStep] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return sessionStorage.getItem(COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });
  const [expandedSteps, setExpandedSteps] = useState({});

  const loadPendingFollowups = useCallback(async () => {
    try {
      const data = await fetchPendingFollowups();
      setPendingEvents(data?.events || []);
    } catch (err) {
      console.error("Error loading pending follow-ups:", err);
      setPendingEvents([]);
    } finally {
      setHasLoadedOnce(true);
    }
  }, []);

  useEffect(() => {
    loadPendingFollowups();
  }, [loadPendingFollowups]);

  const toggleCollapsed = () => {
    const next = !isCollapsed;
    setIsCollapsed(next);
    try {
      sessionStorage.setItem(COLLAPSED_KEY, String(next));
    } catch (_) {}
  };

  const toggleStep = (stepId) => {
    setExpandedSteps((prev) => ({ ...prev, [stepId]: !prev[stepId] }));
  };

  const handleCompleteStep = async (stepId) => {
    try {
      setCompletingStep(stepId);
      await completeFollowupStep(stepId);
      await loadPendingFollowups();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error("Error completing step:", err);
    } finally {
      setCompletingStep(null);
    }
  };

  const handleIgnoreStep = async (stepId) => {
    if (!window.confirm("Dismiss this step? It will be hidden from your dashboard.")) return;
    try {
      setIgnoringStep(stepId);
      await ignoreFollowupStep(stepId);
      await loadPendingFollowups();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error("Error ignoring step:", err);
    } finally {
      setIgnoringStep(null);
    }
  };

  const handleActionLink = (url) => {
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  const totalSteps = pendingEvents.reduce((acc, ev) => acc + (ev.steps?.length || 0), 0);

  if (!hasLoadedOnce) return null;

  if (pendingEvents.length === 0) return null;

  return (
    <Box sx={{ border: "1px solid #e2e8f0", borderRadius: '12px', mb: 3, overflow: "hidden", bgcolor: "#fff", boxShadow: '0 1px 3px rgba(15,23,42,0.04)' }}>
      <Box
        component="button"
        onClick={toggleCollapsed}
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          border: "none",
          background: "none",
          cursor: "pointer",
          textAlign: "left",
          borderBottom: isCollapsed ? 'none' : '1px solid #f1f5f9',
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flex: 1 }}>
          <Box sx={{ bgcolor: `${theme.primary}15`, p: 0.5, borderRadius: '8px', display: 'flex' }}>
            <FlagIcon sx={{ color: theme.primary, fontSize: 18 }} />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>Follow-Up Actions</Typography>
          {totalSteps > 0 && (
            <Box sx={{ bgcolor: '#f1f5f9', color: '#475569', px: 1, py: 0.25, borderRadius: '10px', fontSize: "12px", fontWeight: 700, border: '1px solid #e2e8f0' }}>
              {totalSteps}
            </Box>
          )}
        </Box>
        {isCollapsed ? <ExpandMoreIcon sx={{ color: '#64748b' }} /> : <ExpandLessIcon sx={{ color: '#64748b' }} />}
      </Box>

      {!isCollapsed && (
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          {pendingEvents.map((event) => (
            <Box key={event.event_id} sx={{ mb: 3, '&:last-of-type': { mb: 1 } }}>
              <Box sx={{ mb: 1.5 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#0f172a", lineHeight: 1.3 }}>{event.event_title}</Typography>
                {event.event_end_datetime && (
                  <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.25 }}>{formatEventEndTime(event.event_end_datetime)}</Typography>
                )}
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {(event.steps || []).map((step, idx) => {
                  const expanded = expandedSteps[step.id] !== false;
                  return (
                    <Box 
                      key={step.id} 
                      sx={{ 
                        border: "1px solid #e2e8f0", 
                        borderRadius: '10px',
                        overflow: 'hidden',
                        bgcolor: expanded ? '#f8fafc' : '#fff',
                        transition: 'background-color 0.2s ease',
                      }}
                    >
                      <Box 
                        component="button"
                        onClick={() => toggleStep(step.id)}
                        sx={{ 
                          width: '100%',
                          display: "flex", 
                          alignItems: "flex-start", 
                          justifyContent: "space-between", 
                          gap: 1.5,
                          p: 1.5,
                          border: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          textAlign: 'left',
                        }}
                      >
                        <Box sx={{ display: "flex", gap: 1.25, flex: 1, minWidth: 0, pt: 0.25 }}>
                          <Box sx={{ 
                            width: 24, 
                            height: 24, 
                            borderRadius: '50%', 
                            bgcolor: '#e2e8f0', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>{idx + 1}</Typography>
                          </Box>
                          <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#0f172a', lineHeight: 1.4, pt: '2px' }}>
                            {step.title}
                          </Typography>
                        </Box>
                        <Box sx={{ pt: 0.25 }}>
                          {expanded ? <ExpandLessIcon sx={{ color: '#94a3b8', fontSize: 20 }} /> : <ExpandMoreIcon sx={{ color: '#94a3b8', fontSize: 20 }} />}
                        </Box>
                      </Box>

                      {expanded && (
                        <Box sx={{ px: 1.5, pb: 1.5, pl: { xs: 1.5, sm: 5.5 } }}>
                          {step.description && (
                            <Typography sx={{ fontSize: 14, color: "#334155", mb: 2, lineHeight: 1.5 }}>
                              {step.description}
                            </Typography>
                          )}
                          
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {step.action_link && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  endIcon={<OpenInNewIcon sx={{ fontSize: '16px !important' }} />}
                                  onClick={() => handleActionLink(step.action_link)}
                                  sx={{ 
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    color: theme.primary, 
                                    borderColor: `${theme.primary}50`,
                                    borderRadius: '8px',
                                    px: 1.5,
                                    "&:hover": { borderColor: theme.primary, bgcolor: `${theme.primary}08` } 
                                  }}
                                >
                                  Open Link
                                </Button>
                              )}
                              <Button
                                size="small"
                                variant="contained"
                                disableElevation
                                startIcon={completingStep === step.id ? <CircularProgress size={14} color="inherit" /> : <CheckIcon sx={{ fontSize: '16px !important' }} />}
                                disabled={completingStep !== null}
                                onClick={() => handleCompleteStep(step.id)}
                                sx={{ 
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  bgcolor: theme.primary, 
                                  borderRadius: '8px',
                                  px: 1.5,
                                  "&:hover": { bgcolor: "#065f46" },
                                  "&.Mui-disabled": { bgcolor: `${theme.primary}50`, color: '#fff' }
                                }}
                              >
                                Mark Complete
                              </Button>
                            </Box>
                            
                            <Button
                              size="small"
                              variant="text"
                              disabled={ignoringStep !== null}
                              onClick={() => handleIgnoreStep(step.id)}
                              sx={{
                                textTransform: 'none',
                                fontWeight: 500,
                                color: '#64748b',
                                minWidth: 0,
                                px: 1,
                                "&:hover": { color: '#dc2626', bgcolor: '#fef2f2' }
                              }}
                            >
                              {ignoringStep === step.id ? <CircularProgress size={14} color="inherit" /> : 'Dismiss'}
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ))}
          
          <Box sx={{ borderTop: '1px solid #f1f5f9', mx: -2, px: 2, py: 1.5, display: 'flex', justifyContent: 'center' }}>
            <Button
              component={Link}
              to="/events"
              variant="text"
              sx={{ 
                textTransform: 'none', 
                fontWeight: 600, 
                color: theme.primary,
                '&:hover': { bgcolor: `${theme.primary}10` }
              }}
            >
              View All Events
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
